import { Injectable, inject } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { OpenConfirmationModalService } from '@vendasta/galaxy/confirmation-modal';
import { ConversationApiService } from '@vendasta/conversation';
import { firstValueFrom, throwError, catchError } from 'rxjs';

import { AiKnowledgeService } from './ai-knowledge.service';
import { ApplicationSelectorComponent } from '../application-selector/application-selector.component';
import { KnowledgeSourceWithSuggestedFlag, WebChatWidgetAppType } from '../ai-knowledge-util';

export interface WebChatWidgetContext {
  dialogRef: MatDialogRef<any>;
  knowledgeSource: KnowledgeSourceWithSuggestedFlag | null;
  applicationSelector: ApplicationSelectorComponent;
}

@Injectable()
export class WebChatWidgetService {
  private readonly knowledgeService = inject(AiKnowledgeService);
  private readonly conversationApi = inject(ConversationApiService);
  private readonly confirmationModal = inject(OpenConfirmationModalService);
  private readonly translateService = inject(TranslateService);
  private readonly router = inject(Router);

  async createWebChatWidget(context: WebChatWidgetContext): Promise<void> {
    context.applicationSelector?.isCreatingWidget.set(true);
    const resp = await firstValueFrom(
      this.conversationApi
        .createWidget({
          name: 'Web Chat',
          namespace: this.knowledgeService.accountGroupId() || this.knowledgeService.partnerId(),
          widgetType: 'inbox',
        })
        .pipe(
          catchError((err) => {
            this.confirmationModal
              .openModal({
                title: this.translateService.instant('CREATE_WEB_CHAT_WIDGET.ERROR.TITLE'),
                message: this.translateService.instant('CREATE_WEB_CHAT_WIDGET.ERROR.MESSAGE'),
                confirmButtonText: this.translateService.instant('CREATE_WEB_CHAT_WIDGET.ERROR.YES'),
                cancelButtonText: this.translateService.instant('COMMON.NO'),
              })
              .subscribe((confirmed) => {
                context.applicationSelector?.isCreatingWidget.set(false);
                if (confirmed) {
                  this.createWebChatWidget(context);
                }
              });
            return throwError(() => err);
          }),
        ),
    );

    const widgetId = resp.widget.widgetId;
    const configurationUrl = await this.buildAiKnowledgeAppConfigurationUrl(widgetId);
    await this.knowledgeService
      .upsertKnowledgeForApp(this.buildAppId(widgetId), resp.widget.name, configurationUrl, WebChatWidgetAppType, [
        {
          id: context.knowledgeSource?.id,
          namespace: context.knowledgeSource?.namespace,
        },
      ])
      .catch(
        // ignore error here for now because it could orphan the widget otherwise
        () => void 0,
      );
    this.confirmationModal
      .openModal({
        title: this.translateService.instant('CREATE_WEB_CHAT_WIDGET.SUCCESS.TITLE'),
        confirmButtonText: this.translateService.instant('CREATE_WEB_CHAT_WIDGET.SUCCESS.GO_TO_WEB_CHAT_SETTINGS'),
        cancelButtonText: this.translateService.instant('CREATE_WEB_CHAT_WIDGET.SUCCESS.KEEP_EDITING'),
      })
      .subscribe((confirmed) => {
        if (confirmed) {
          context.dialogRef.close(true);
          this.router.navigate(this.knowledgeService.buildWebChatWidgetEditRoute(widgetId));
        } else {
          context.applicationSelector.loadApps();
          context.applicationSelector?.isCreatingWidget.set(false);
        }
      });
  }

  async buildAiKnowledgeAppConfigurationUrl(widgetId: string): Promise<string> {
    if (this.knowledgeService.accountGroupId()) {
      return `/account/location/${this.knowledgeService.accountGroupId()}/settings/widgets/${widgetId}/edit`;
    }
    return `/(inbox:inbox/widgets/${widgetId}/edit)`;
  }

  buildAppId(widgetId: string): string {
    return `APP-WEBCHAT-${widgetId}`;
  }
}
